import Location from '@r6digital/rapidstor-types/react/entities/Location';

import {
    LoqateAddressFindResponseObject,
    LoqateAddressRetrieveResponseObject,
    LoqateBankResponseObject,
    loqateEmailFailureResponses,
    LoqateEmailResponseObject,
} from '@/core/types/Loqate';
import { WizardValidationError } from '@/core/types/Wizard';
import Logger from '@/core/utils/logging';

import BaseService from '@/features/rapidstor/services/BaseService';

export default class LoqateValidator extends BaseService {
    form: Record<string, string>;

    errors: WizardValidationError[];

    apiBaseUrl: string;

    addressFindRoute: string;

    addressRetrieveRoute: string;

    emailValidateRoute: string;

    bankValidateRoute: string;

    constructor(form: Record<string, string>, location: Location) {
        super(location);
        this.apiBaseUrl = 'https://api.addressy.com';
        this.addressFindRoute = '/Capture/Interactive/Find/v1.10/json3.ws';
        this.addressRetrieveRoute = '/Capture/Interactive/Retrieve/v1.20/json3.ws';
        this.emailValidateRoute = '/EmailValidation/Interactive/Validate/v2.00/json3.ws';
        this.bankValidateRoute = '/BankAccountValidation/Interactive/Validate/v2/json3.ws';
        this.form = form;
        this.errors = [];
    }

    /**
   * Get API key
   *
   * @returns {string | undefined}
   */
    get apiKey(): string | undefined {
        return window.rapidstor_settings.rapidstor_options.loqate?.apiKey;
    }

    async validateBank(bsb: string, accountNumber: string) {
        if (!this.apiKey) {
            Logger.error('Loqate API key is undefined', this._location);
            return false;
        }

        this.errors = [];

        const bankRequest = await fetch(
            `${this.apiBaseUrl}${this.bankValidateRoute}?Key=${this.apiKey}&AccountNumber=${accountNumber}&SortCode=${bsb}`,
        );

        if (!bankRequest.ok) {
            this.errors.push({
                name: 'bankAccountNumber',
                message: 'Invalid Bank Account Details',
            });

            return false;
        }

        const bankResponse = (await bankRequest.json()) as LoqateBankResponseObject;

        if (bankResponse.Items.length === 0) {
            this.errors.push({
                name: 'bank',
                message: 'Invalid Bank Account Details',
            });

            return false;
        }

        const bankItem = bankResponse.Items[0];

        if (!bankItem.IsCorrect) {
            this.errors.push({
                name: 'bank',
                message: 'Invalid Bank Account Details',
            });

            return false;
        }

        return bankItem;
    }

    /**
   * Validate Email
   *
   * @param {string} email
   *
   * @returns {boolean}
   */
    async validateEmail(email: string) {
        if (!this.apiKey) {
            Logger.error('Loqate API key is undefined', this._location);
            return false;
        }

        this.errors = [];

        const emailRequest = await fetch(
            `${this.apiBaseUrl}${this.emailValidateRoute}?Key=${this.apiKey}&Email=${email}`,
        );
        if (!emailRequest.ok) {
            this.errors.push({
                name: 'email',
                message: 'Invalid email address',
            });

            return false;
        }

        const emailResponse = (await emailRequest.json()) as LoqateEmailResponseObject;

        if (emailResponse.Items.length === 0) {
            this.errors.push({
                name: 'email',
                message: 'Invalid email address',
            });

            return false;
        }

        const emailItem = emailResponse.Items[0];
        if (loqateEmailFailureResponses.includes(emailItem.ResponseCode)) {
            this.errors.push({
                name: 'email',
                message: 'Invalid email address',
            });

            return false;
        }

        return true;
    }

    /**
   * Validate Address
   *
   * @param {string} address
   *
   * @returns {boolean}
   */
    async validateResidential(
        address: string,
        type: 'Address' | 'Postcode',
        container?: string,
        alternate?: boolean,
    ) {
        if (!this.apiKey) {
            Logger.error('Loqate API key is undefined', this._location);

            return false;
        }

        this.errors = [];

        let url = `${this.apiBaseUrl}${this.addressFindRoute}?Key=${
            this.apiKey
        }&Text=${address}&Countries=${this._location.rapidstor.country}&Bias=false&IsMiddleware=true`;

        if (container) {
            url += `&Container=${container}`;
        }

        const addressRequest = await fetch(url);

        if (!addressRequest.ok) {
            this.errors.push({
                name: alternate ? 'address_alternate' : 'address',
                message: alternate
                    ? 'Address Alternate is invalid'
                    : 'Address is invalid',
            });

            return false;
        }
        this.errors = [];

        const addressResponse = (await addressRequest.json()) as LoqateAddressFindResponseObject;

        if (addressResponse.Items.length === 0) {
            this.errors.push({
                name: alternate ? 'address_alternate' : 'address',
                message: alternate
                    ? 'Address Alternate is invalid'
                    : 'Address is invalid',
            });

            return false;
        }
        this.errors = [];

        const addressItems = addressResponse.Items.filter(
            (item) => item.Type === type,
        );

        return addressItems;
    }

    async retrieveAddress(id: string) {
        if (!this.apiKey) {
            Logger.error('Loqate API key is undefined', this._location);

            return false;
        }

        this.errors = [];

        const addressRetrieveRequest = await fetch(
            `${this.apiBaseUrl}${this.addressRetrieveRoute}?Key=${this.apiKey}&Id=${id}`,
        );

        if (!addressRetrieveRequest.ok) {
            this.errors.push({
                name: 'address',
                message: 'Address is invalid',
            });

            return false;
        }

        const addressRetrieveResponse = (await addressRetrieveRequest.json()) as LoqateAddressRetrieveResponseObject;

        if (addressRetrieveResponse.Items.length === 0) {
            this.errors.push({
                name: 'address',
                message: 'Address is invalid',
            });

            return false;
        }

        if (!addressRetrieveResponse.Items[0].Line1) {
            return false;
        }

        const items = addressRetrieveResponse.Items;

        return items[0];
    }
}
